import { useDisclosure } from '@mantine/hooks';
import ImportXlsxModal, { ImportXlsxModalProps } from 'components/modals/import-xlsx-modal/ImportXlsxModal';
import { createContext, useContext, useMemo, useState } from 'react';

type OpenImportXlsxModalParams = Omit<ImportXlsxModalProps, 'opened' | 'onClose'>;
type OpenImportXlsxModal = (params: OpenImportXlsxModalParams) => void;

interface IImportXlsxModalContext {
  openImportXlsxModal: OpenImportXlsxModal;
}

const ImportXlsxModalContext = createContext<IImportXlsxModalContext>(undefined!);

/**
 * Provider for opening the XLSX import modal.
 */
export default function ImportXlsxModalProvider({ children }: { children: React.ReactNode }) {
  const [opened, { close, open }] = useDisclosure(false);
  const [params, setParams] = useState<OpenImportXlsxModalParams>(undefined!);

  /**
   * Opens the XLSX import modal with optional params.
   */
  const openImportXlsxModal: OpenImportXlsxModal = (params) => {
    setParams(params);
    open();
  };

  const value = useMemo(() => ({ openImportXlsxModal }), [openImportXlsxModal]);

  return (
    <ImportXlsxModalContext.Provider value={value}>
      {children}
      {opened && <ImportXlsxModal opened onClose={close} {...params!} />}
    </ImportXlsxModalContext.Provider>
  );
}

/**
 * Hook to open the XLSX import modal.
 */
export function useImportXlsxModal() {
  return useContext(ImportXlsxModalContext);
}
